import type { GameRules, MatchOutcome, MatchSetup, RenderFrame, Rng } from "@obean/game-contract";
import { FixedStepper, createRng } from "@obean/sim-core";

export interface DriverFrame<S> extends RenderFrame<S> {
  outcome: MatchOutcome | null;
}

/** Produces what to draw each display frame: the simulation in this browser, or an online match. */
export interface MatchDriver<S> {
  /** Local play (the simulation in this browser) or an online match (perf_sample's `mode`). */
  readonly mode: "local" | "online";
  frame(now: number): DriverFrame<S>;
  /** Online: perf_sample's netcode props for the time since the last call (docs/metrics.md). */
  netStats?(): Record<string, number | null>;
  /** Online: the netcode's numbers now, for a trace (ADR 0035); reading resets nothing. */
  netNow?(): Record<string, number | null>;
  /** Online: let go of every control now, as the page goes to the background (P2-WEB-06). */
  release?(): void;
}

/**
 * Local play: the authoritative simulation runs in this browser, on the same rules the server runs.
 * Used by the sandbox page today and by the local/offline track later (ADR 0014).
 */
export class LocalDriver<S, I> implements MatchDriver<S> {
  readonly mode = "local";
  readonly #rules: GameRules<S, I>;
  readonly #rng: Rng;
  readonly #stepper: FixedStepper;
  readonly #input: (slot: number, tick: number) => I;
  readonly #slots: number;
  #state: S;
  #prev: S;
  #tick = 0;
  #outcome: MatchOutcome | null = null;

  constructor(
    rules: GameRules<S, I>,
    setup: MatchSetup,
    input: (slot: number, tick: number) => I,
    now: () => number,
  ) {
    this.#rules = rules;
    this.#rng = createRng(setup.seed);
    this.#state = rules.init(setup, this.#rng);
    this.#prev = this.#clone(this.#state);
    this.#input = input;
    this.#slots = setup.players.length;
    this.#stepper = new FixedStepper({ stepMs: 1000 / rules.manifest.tickHz, now });
  }

  /** A deep copy through the snapshot codec (`step` may mutate its state). */
  #clone(state: S): S {
    return this.#rules.decodeSnapshot(this.#rules.encodeSnapshot(state, this.#rng), createRng(0));
  }

  frame(): DriverFrame<S> {
    if (!this.#outcome)
      for (let due = this.#stepper.due(); due > 0; due--) {
        this.#prev = this.#clone(this.#state);
        const inputs = Array.from({ length: this.#slots }, (_, slot) =>
          this.#input(slot, this.#tick),
        );
        this.#state = this.#rules.step(this.#state, inputs, this.#rng);
        this.#tick++;
        this.#outcome = this.#rules.outcome(this.#state);
        if (this.#outcome) break;
      }
    const alpha = this.#outcome ? 1 : Math.min(1, this.#stepper.lagMs / this.#stepper.stepMs);
    return { from: this.#prev, to: this.#state, alpha, local: null, outcome: this.#outcome };
  }

  get tick(): number {
    return this.#tick;
  }

  /** The current authoritative state (read-only use: debugging and tests). */
  get state(): S {
    return this.#state;
  }
}
